import React, { useCallback, useEffect, useRef } from 'react'
import style from './style/FontRuler.scss'
import _ from 'lodash'

/**
 * Adopted from:
 * "Web font loading detection, without timers"
 * [http://smnh.me/web-font-loading-detection-without-timers/]
 * A blog post by Simon Hanukaev [https://github.com/smnh]
 */

type FontRulerProps = {
	fontFamily: string
	onFontLoaded: (fontFamily: string) => void
}
type ElementMeasurements = { offsetWidth: number; offsetHeight: number }

function hasArea(measurements: ElementMeasurements | null) {
	return !!(measurements?.offsetWidth && measurements?.offsetHeight)
}

function areaChanged(area1: ElementMeasurements, area2: ElementMeasurements) {
	return area1.offsetWidth !== area2.offsetWidth || area1.offsetHeight !== area2.offsetHeight
}

function cloneArea(elem: HTMLDivElement) {
	return (
		elem && {
			offsetWidth: elem.offsetWidth,
			offsetHeight: elem.offsetHeight,
		}
	)
}

export const FontRuler = ({ fontFamily, onFontLoaded }: FontRulerProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const innerWrapperRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const innerContentRef = useRef<HTMLDivElement>(null)

	// todo - single ref ?
	const rafCallbackId = useRef<number>(0) // requestAnimationFrame callback id
	const contentNodeOrigSize = useRef<{ offsetWidth: number; offsetHeight: number } | null>(null)
	const trigger = useRef(false) // can be set to true on updateCurrentSize, except first invocation
	const triggeredResize = useRef(false) // to only onLoadCallback once
	const handleFontResizeDebounced = useRef<ReturnType<typeof _.debounce> | null>(null)

	const updateCurrentSize = useCallback(() => {
		rafCallbackId.current = 0

		const wrapperNode = wrapperRef.current
		if (!wrapperNode) {
			return
		}
		const contentNode = contentRef.current as HTMLDivElement
		const innerContentNode = innerContentRef.current as HTMLDivElement
		const innerWrapperNode = innerWrapperRef.current as HTMLDivElement

		const origSize = cloneArea(wrapperNode)

		// Resize wrapper and scroll its content to the bottom right corner
		wrapperNode.style.width = `${origSize.offsetWidth - 1}px`
		wrapperNode.style.height = `${origSize.offsetHeight - 1}px`
		wrapperNode.scrollLeft = wrapperNode.scrollWidth - wrapperNode.clientWidth
		wrapperNode.scrollTop = wrapperNode.scrollHeight - wrapperNode.clientHeight

		// Resize inner content and scroll inner wrapper to the bottom right corner
		innerContentNode.style.width = `${origSize.offsetWidth + 1}px`
		innerContentNode.style.height = `${origSize.offsetHeight + 1}px`
		innerWrapperNode.scrollLeft = innerWrapperNode.scrollWidth - innerWrapperNode.clientWidth
		innerWrapperNode.scrollTop = innerWrapperNode.scrollHeight - innerWrapperNode.clientHeight

		if (hasArea(contentNode)) {
			if (contentNodeOrigSize.current) {
				trigger.current = areaChanged(contentNode, contentNodeOrigSize.current)
			} else {
				contentNodeOrigSize.current = cloneArea(contentNode)
			}
			contentNode.style.fontFamily = `${fontFamily}, serif`
		}
	}, [fontFamily])

	const handleFontResize = useCallback(() => {
		// already invoked onFontLoaded or no needed anymore (unmounted)
		if (triggeredResize.current || !contentRef.current) {
			return
		}

		let _trigger = trigger.current
		if (!_trigger) {
			const contentNode = contentRef.current!
			_trigger = hasArea(contentNodeOrigSize.current) && areaChanged(contentNode, contentNodeOrigSize.current!)
		}

		if (_trigger) {
			if (rafCallbackId.current) {
				cancelAnimationFrame(rafCallbackId.current)
				rafCallbackId.current = 0
			}
			triggeredResize.current = true
			onFontLoaded(fontFamily)
		} else if (!rafCallbackId.current) {
			rafCallbackId.current = requestAnimationFrame(updateCurrentSize)
		}
	}, [fontFamily, onFontLoaded, updateCurrentSize])

	const handleScroll = (e: Event) => {
		e.stopPropagation()
		e.preventDefault()
		handleFontResizeDebounced.current!()
	}

	useEffect(() => {
		handleFontResizeDebounced.current = _.debounce(handleFontResize, 32, {
			leading: false,
			trailing: true,
		})
		wrapperRef.current!.addEventListener('scroll', (e) => handleScroll(e), true)
		innerWrapperRef.current!.addEventListener('scroll', (e) => handleScroll(e), true)
		updateCurrentSize()
		return () => {
			handleFontResizeDebounced.current!.cancel()
			if (rafCallbackId.current) {
				cancelAnimationFrame(rafCallbackId.current)
			}
		}
	}, [fontFamily, updateCurrentSize, handleFontResize])

	return (
		<div className={style.wrapper} ref={wrapperRef}>
			<div className={style.content} ref={contentRef}>
				<div className={style.innerWrapper} ref={innerWrapperRef}>
					<div ref={innerContentRef} />
				</div>
				<span className={style.fontRulerContent} />
			</div>
		</div>
	)
}

export default FontRuler
